import { useEffect, useRef } from 'react';

/**
 * Hook to fix iOS Safari keyboard scroll issues and viewport offset bug
 * Fixes iOS 26 Safari bug where visualViewport.offsetTop doesn't reset after keyboard closes
 *
 * @param enabled - Whether the hook should be active (default: true)
 */
export const usePreventKeyboardScroll = (enabled: boolean = true) => {
  const initialViewportHeightRef = useRef<number | null>(null);
  const scrollToTopTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingScrollTargetRef = useRef<HTMLElement | null>(null);
  const keyboardOpeningRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) return;

    const isMobile = window.innerWidth < 768;
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

    if (!isMobile || !isIOS) return;

    let isKeyboardOpen = false;

    const fixViewportOffset = () => {
      // Only fix the iOS visualViewport offset bug, don't force scroll to top
      if (window.visualViewport) {
        const offsetTop = window.visualViewport.offsetTop;
        if (offsetTop > 0) {
          // Fix the offset bug by scrolling up to compensate
          window.scrollBy(0, -offsetTop);

          // Double check with requestAnimationFrame
          requestAnimationFrame(() => {
            if (window.visualViewport && window.visualViewport.offsetTop > 0) {
              window.scrollBy(0, -window.visualViewport.offsetTop);
            }
          });
        }
      }
    };

    // Helper function to find the closest scrollable parent or popup container
    const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
      let parent = element.parentElement;

      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        const position = style.position;

        // Check if this element is scrollable
        const isScrollable =
          (overflowY === 'auto' || overflowY === 'scroll' ||
           overflowX === 'auto' || overflowX === 'scroll') &&
          parent.scrollHeight > parent.clientHeight;

        // Check if this is a positioned container (popup/modal/dropdown)
        // These containers often have inputs that need special scroll handling
        const isPositionedContainer =
          (position === 'absolute' || position === 'fixed') &&
          (parent.classList.contains('date-filter') ||
           parent.classList.contains('mantine-Modal-content') ||
           parent.classList.contains('mantine-Popover-dropdown') ||
           parent.hasAttribute('data-mantine-portal'));

        if (isScrollable || isPositionedContainer) {
          return parent;
        }

        parent = parent.parentElement;
      }

      return document.documentElement;
    };

    // Perform the scroll operation for an input element
    const performScroll = (target: HTMLElement) => {
      // Check if input is inside a dropdown
      const isDropdownInput =
        target.hasAttribute('data-dropdown-search') ||
        target.closest('.container-select') !== null ||
        target.closest('[role="listbox"]') !== null ||
        target.closest('.dropdown') !== null;

      // Only scroll if NOT a dropdown input (dropdown handles its own scrolling)
      if (!isDropdownInput) {
        // Find the closest scrollable container (could be popup, modal, or viewport)
        const scrollableParent = findScrollableParent(target);

        if (scrollableParent && scrollableParent !== document.documentElement) {
          // For popups/modals with position absolute/fixed
          // We need to account for visualViewport when keyboard is open

          if (window.visualViewport) {
            // Use visualViewport to get accurate viewport dimensions
            const visualViewportHeight = window.visualViewport.height;
            const visualViewportOffsetTop = window.visualViewport.offsetTop;

            // Get target position relative to visual viewport
            const targetRect = target.getBoundingClientRect();

            // Adjust for visual viewport offset (iOS keyboard offset bug)
            const adjustedTargetTop = targetRect.top - visualViewportOffsetTop;
            const adjustedTargetBottom = targetRect.bottom - visualViewportOffsetTop;

            // Check if input is hidden by keyboard or outside visual viewport
            const isHiddenByKeyboard = adjustedTargetBottom > visualViewportHeight;
            const isAboveViewport = adjustedTargetTop < 0;

            if (isHiddenByKeyboard || isAboveViewport) {
              // Calculate the desired scroll position
              // We want to position the input in the center of the VISIBLE area (visual viewport)
              const targetCenterY = (targetRect.top + targetRect.bottom) / 2;
              const desiredCenterY = visualViewportHeight / 2;
              const scrollAdjustment = targetCenterY - desiredCenterY;

              // Apply scroll to the container
              scrollableParent.scrollTo({
                top: scrollableParent.scrollTop + scrollAdjustment,
                behavior: 'smooth'
              });
            }
          } else {
            // Fallback for browsers without visualViewport
            const targetRect = target.getBoundingClientRect();
            const containerRect = scrollableParent.getBoundingClientRect();

            const isAboveView = targetRect.top < containerRect.top;
            const isBelowView = targetRect.bottom > containerRect.bottom;

            if (isAboveView || isBelowView) {
              const targetOffsetTop = target.offsetTop;
              const containerHeight = scrollableParent.clientHeight;
              const targetHeight = target.offsetHeight;
              const scrollToPos = targetOffsetTop - (containerHeight / 2) + (targetHeight / 2);

              scrollableParent.scrollTo({
                top: scrollToPos,
                behavior: 'smooth'
              });
            }
          }
        } else {
          // For regular inputs, scroll into view normally
          // Use scrollIntoViewIfNeeded for WebKit if available, otherwise scrollIntoView
          if ('scrollIntoViewIfNeeded' in target && typeof (target as any).scrollIntoViewIfNeeded === 'function') {
            (target as any).scrollIntoViewIfNeeded(true);
          } else {
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          }
        }
      }
    };

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;

      // Only handle input elements
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // CRITICAL FIX: Prevent iOS Safari's automatic scroll on first focus
        // iOS Safari won't auto-scroll if the element has opacity: 0
        // This is the key to preventing the unwanted scroll behavior
        const originalOpacity = target.style.opacity;
        target.style.opacity = '0';

        // Restore opacity immediately after the focus event completes
        // This prevents the auto-scroll while keeping the input visible
        requestAnimationFrame(() => {
          target.style.opacity = originalOpacity || '1';
        });

        // Store initial viewport height when keyboard opens
        if (window.visualViewport && !isKeyboardOpen) {
          initialViewportHeightRef.current = window.visualViewport.height;
          keyboardOpeningRef.current = true;
        }

        isKeyboardOpen = true;

        // Clear any pending scroll-to-top timeouts
        if (scrollToTopTimeoutRef.current) {
          clearTimeout(scrollToTopTimeoutRef.current);
          scrollToTopTimeoutRef.current = null;
        }

        // Store the target for the viewport resize handler to use
        pendingScrollTargetRef.current = target;

        // Now manually control the scroll after preventing the auto-scroll
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            performScroll(target);
          });
        });
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (!isKeyboardOpen) return;

      const target = e.target as HTMLElement;

      // Check if the element losing focus is a dropdown input
      const isDropdownInput =
        target.hasAttribute('data-dropdown-search') ||
        target.closest('.container-select') !== null;

      // If dropdown input, let dropdown handle scroll (don't run global scroll)
      if (isDropdownInput) {
        isKeyboardOpen = false;
        return;
      }

      // Don't immediately scroll to top on focusout
      // Wait for viewport resize to confirm keyboard actually closed
      // This prevents unwanted scrolling when user taps another input
      // The viewport resize handler will detect when keyboard actually closes
    };

    // Listen for visualViewport resize to detect keyboard open/close
    const handleViewportResize = () => {
      if (!window.visualViewport) return;

      const viewportHeight = window.visualViewport.height;
      const windowHeight = window.innerHeight;
      const heightDifference = Math.abs(viewportHeight - windowHeight);

      // Keyboard is opening (viewport is getting smaller)
      if (heightDifference > 100 && keyboardOpeningRef.current && pendingScrollTargetRef.current) {
        // The keyboard animation has started/completed
        // Now we can reliably scroll to the input
        keyboardOpeningRef.current = false;

        // iOS Safari needs longer delay for keyboard animation to fully complete
        // and for getBoundingClientRect to return correct values
        setTimeout(() => {
          if (pendingScrollTargetRef.current) {
            performScroll(pendingScrollTargetRef.current);
            // Clear the pending target after scroll completes
            setTimeout(() => {
              pendingScrollTargetRef.current = null;
            }, 200);
          }
        }, 300);
      }
      // If viewport height is close to window height, keyboard is likely closed
      else if (heightDifference < 100) {
        // Only fix offset if keyboard was actually open
        if (isKeyboardOpen || initialViewportHeightRef.current) {
          isKeyboardOpen = false;
          keyboardOpeningRef.current = false;
          pendingScrollTargetRef.current = null;

          // Clear any existing timeout
          if (scrollToTopTimeoutRef.current) {
            clearTimeout(scrollToTopTimeoutRef.current);
          }

          // Only fix the viewport offset bug, don't force scroll to top
          // This preserves user's scroll position while fixing the iOS bug
          scrollToTopTimeoutRef.current = setTimeout(() => {
            fixViewportOffset();
            initialViewportHeightRef.current = null;
          }, 150);
        }
      }
    };

    // Listen for focus events
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    window.visualViewport?.addEventListener('resize', handleViewportResize);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      window.visualViewport?.removeEventListener('resize', handleViewportResize);

      if (scrollToTopTimeoutRef.current) {
        clearTimeout(scrollToTopTimeoutRef.current);
      }

      // Clear refs
      pendingScrollTargetRef.current = null;
      keyboardOpeningRef.current = false;
      initialViewportHeightRef.current = null;
    };
  }, [enabled]);
};
